import { ElementType, FC } from 'react';
import { alpha, styled, Typography, TypographyProps } from '@mui/material';
import DOMPurify from 'dompurify';

import useMetadataContext from '@hooks/useMetadataContext';
import { DetailedTitle } from '@models/Title';

export const Highlighted = styled(Typography)`
  & > em {
    background: ${({ theme }) => alpha(theme.palette.info.main, 0.4)};
    color: ${({ theme }) => theme.palette.text.primary};
    font-style: normal;
    font-weight: bold;
    padding: 0 0.5px;

    + em {
      padding: 0 0.5px;
    }
  }
`;

type HighlightedProps = TypographyProps & {
  asset: Pick<DetailedTitle, 'masterId'> & Partial<DetailedTitle>;
  component?: ElementType;
  field: keyof DetailedTitle;
};

// The backend returns matching sentences, so reapply their highlights to the complete field text.
const mergeHighlightsWithFullText = (text: string, snippets: string[]) => {
  const container = document.createElement('span');
  container.textContent = text;

  return snippets.reduce((fullText, snippet) => {
    const sanitizedSnippet = DOMPurify.sanitize(snippet, { ALLOWED_TAGS: ['em'] });

    container.innerHTML = sanitizedSnippet;
    const snippetText = container.textContent;
    container.textContent = snippetText;

    return snippetText ? fullText.replaceAll(container.innerHTML, sanitizedSnippet) : fullText;
  }, container.innerHTML);
};

const HighlightedTypography: FC<HighlightedProps> = ({ field, asset: title, ...rest }) => {
  const { highlights } = useMetadataContext();
  const text = title[field];
  const fieldHighlights = highlights?.[title.masterId]?.[field];

  return fieldHighlights && typeof text === 'string' ? (
    <Highlighted
      component='span'
      dangerouslySetInnerHTML={{
        __html: mergeHighlightsWithFullText(text, fieldHighlights),
      }}
      variant='inherit'
      {...rest}
    />
  ) : (
    <Typography {...rest}>{text as string}</Typography>
  );
};

export default HighlightedTypography;
